import clsx from 'clsx';
import type { ComponentPropsWithRef, CSSProperties, ElementType, ReactElement, ReactNode } from 'react';

import { RecessedSurface } from '../recessed-surface/RecessedSurface';
import { getTertiaryPressedSurfaceClasses } from '../surface/surfaceClasses';

export type ButtonColor = 'default' | 'danger' | 'muted' | 'secondary';
export type ButtonIconPosition = 'both' | 'leading' | 'trailing';
export type ButtonVariant = 'destructive' | 'link' | 'plain' | 'primary' | 'secondary' | 'tertiary';
type LegacyButtonVariant = 'default' | 'solid';

type ButtonVariantValue = ButtonVariant | LegacyButtonVariant;
type InternalButtonVariant = ButtonVariant | 'muted';

type ButtonSharedProps<E extends ElementType = 'button'> = {
    readonly activeSurface?: 'default' | 'recessed' | undefined;
    readonly as?: E | undefined;
    readonly children?: ReactNode;
    readonly className?: string | undefined;
    readonly disabled?: boolean | undefined;
    readonly disabledVariant?: 'dark' | 'light' | undefined;
    readonly iconOnly?: boolean | undefined;
    readonly iconPosition?: ButtonIconPosition | undefined;
    readonly isActive?: boolean | undefined;
    readonly size?: 'sm' | undefined;
    readonly style?: CSSProperties | undefined;
    readonly type?: 'button' | 'reset' | 'submit' | undefined;
};

type ButtonOwnProps<
    E extends ElementType = 'button',
    V extends ButtonVariantValue | undefined = ButtonVariantValue | undefined,
> = ButtonSharedProps<E> & {
    // Compatibility bridge for the former `variant="solid" color="..."` API.
    readonly color?: ButtonColor | undefined;
    // Supports canonical semantic values plus legacy values for gradual migration.
    readonly variant?: V | undefined;
};

export type ButtonProps<
    E extends ElementType = 'button',
    V extends ButtonVariantValue | undefined = ButtonVariantValue | undefined,
> = ButtonOwnProps<E, V> & Omit<ComponentPropsWithRef<E>, keyof ButtonOwnProps<E>>;

export const BUTTON_SM_ICON_ONLY_SIZE = 'var(--spacing-control-sm)';

const SMALL_ICON_ONLY_DIMENSIONS = 'h-[var(--button-sm-icon-only-size)] w-[var(--button-sm-icon-only-size)]';

type ButtonStyle = CSSProperties & {
    readonly '--button-sm-icon-only-size'?: string;
};

const BUTTON_SM_ICON_ONLY_SIZE_STYLE: ButtonStyle = {
    '--button-sm-icon-only-size': BUTTON_SM_ICON_ONLY_SIZE,
};

/* ============================================
 * Size classes: padding + typography
 * ============================================ */
const getSmContentClasses = (iconPosition: ButtonIconPosition | undefined): string =>
    clsx(
        'py-s px-l',
        iconPosition == null && 'gap-s',
        iconPosition === 'leading' && 'gap-xs pl-m',
        iconPosition === 'trailing' && 'gap-xs pr-m',
        iconPosition === 'both' && 'gap-xs pl-m pr-m',
        iconPosition == null &&
            '[&:has(>svg:first-child)]:gap-xs [&:has(>svg:first-child)]:pl-m [&:has(>svg:last-child)]:gap-xs [&:has(>svg:last-child)]:pr-m',
    );

const getSmRecessedContentClasses = (iconPosition: ButtonIconPosition | undefined): string =>
    clsx(
        'py-[calc(var(--spacing-s)-var(--spacing-xxs))] px-[calc(var(--spacing-l)-var(--spacing-xxs))]',
        iconPosition == null && 'gap-s',
        iconPosition === 'leading' && 'gap-xs pl-[calc(var(--spacing-m)-var(--spacing-xxs))]',
        iconPosition === 'trailing' && 'gap-xs pr-[calc(var(--spacing-m)-var(--spacing-xxs))]',
        iconPosition === 'both' &&
            'gap-xs pl-[calc(var(--spacing-m)-var(--spacing-xxs))] pr-[calc(var(--spacing-m)-var(--spacing-xxs))]',
        iconPosition == null &&
            '[&:has(>svg:first-child)]:gap-xs [&:has(>svg:first-child)]:pl-[calc(var(--spacing-m)-var(--spacing-xxs))] [&:has(>svg:last-child)]:gap-xs [&:has(>svg:last-child)]:pr-[calc(var(--spacing-m)-var(--spacing-xxs))]',
    );

const getDefaultContentClasses = (iconPosition: ButtonIconPosition | undefined): string =>
    clsx(
        'py-m px-xl',
        iconPosition == null && 'gap-s',
        iconPosition === 'leading' && 'gap-s pl-l',
        iconPosition === 'trailing' && 'gap-s pr-l',
        iconPosition === 'both' && 'gap-s pl-l pr-l',
        iconPosition == null && '[&:has(>svg:first-child)]:pl-l [&:has(>svg:last-child)]:pr-l',
    );

const getDefaultRecessedContentClasses = (iconPosition: ButtonIconPosition | undefined): string =>
    clsx(
        'py-[calc(var(--spacing-m)-var(--spacing-xxs))] px-[calc(var(--spacing-xl)-var(--spacing-xxs))]',
        iconPosition == null && 'gap-s',
        iconPosition === 'leading' && 'gap-s pl-[calc(var(--spacing-l)-var(--spacing-xxs))]',
        iconPosition === 'trailing' && 'gap-s pr-[calc(var(--spacing-l)-var(--spacing-xxs))]',
        iconPosition === 'both' &&
            'gap-s pl-[calc(var(--spacing-l)-var(--spacing-xxs))] pr-[calc(var(--spacing-l)-var(--spacing-xxs))]',
        iconPosition == null &&
            '[&:has(>svg:first-child)]:pl-[calc(var(--spacing-l)-var(--spacing-xxs))] [&:has(>svg:last-child)]:pr-[calc(var(--spacing-l)-var(--spacing-xxs))]',
    );

const getSizeClasses = (
    size: ButtonProps['size'],
    iconOnly: boolean,
    iconPosition: ButtonIconPosition | undefined,
    variant: InternalButtonVariant,
): string => {
    // Links sit in running text, so they keep the text's height instead of the control height.
    const hasControlHeight = variant !== 'link';

    switch (size) {
        case 'sm':
            return clsx(
                '!rounded-full',
                'text-meta font-medium',
                '[&>svg]:!h-[12px] [&>svg]:!w-[12px]',
                iconOnly
                    ? clsx(
                          hasControlHeight && SMALL_ICON_ONLY_DIMENSIONS,
                          'p-[calc(var(--spacing-s)+var(--spacing-xxs))]',
                      )
                    : clsx(hasControlHeight && 'min-h-control-sm', getSmContentClasses(iconPosition)),
            );
        default:
            return clsx(
                'text-body',
                iconOnly
                    ? 'h-[var(--spacing-7xl)] w-[var(--spacing-7xl)] p-[calc(var(--spacing-m)+var(--spacing-xxs))]'
                    : getDefaultContentClasses(iconPosition),
            );
    }
};

const getRecessedInnerSizeClasses = (
    size: ButtonProps['size'],
    iconOnly: boolean,
    iconPosition: ButtonIconPosition | undefined,
): string => {
    switch (size) {
        case 'sm':
            return clsx(
                '!rounded-full',
                'text-meta font-medium',
                '[&>svg]:!h-[12px] [&>svg]:!w-[12px]',
                iconOnly ? 'p-[var(--spacing-s)]' : getSmRecessedContentClasses(iconPosition),
            );
        default:
            return clsx(
                '!rounded-full text-body',
                iconOnly
                    ? 'h-[calc(var(--spacing-7xl)-var(--spacing-xxs)*2)] w-[calc(var(--spacing-7xl)-var(--spacing-xxs)*2)] p-[var(--spacing-m)]'
                    : getDefaultRecessedContentClasses(iconPosition),
            );
    }
};

/* ============================================
 * Variant helpers (extracted to reduce complexity)
 * ============================================ */
const primaryShadowClass = 'shadow-action-primary hover:shadow-action-primary-hover';

const getPrimaryVariantClasses = (
    size: ButtonProps['size'],
    disabled: boolean,
    disabledVariant: ButtonProps['disabledVariant'],
): string => {
    const disabledBackgroundClass = disabledVariant === 'dark' ? 'bg-action-primary' : 'bg-action-disabled';
    const disabledText = disabledVariant === 'dark' ? 'text-action-on-primary' : 'text-action-on-disabled';

    return clsx(
        disabled ? disabledText : 'text-action-on-primary',
        !disabled && (size === 'sm' ? 'shadow-sm hover:shadow-sm' : primaryShadowClass),
        disabled ? disabledBackgroundClass : 'bg-action-primary',
        !disabled && 'hover:bg-action-primary-hover',
    );
};

const getSecondaryColorClasses = (disabled: boolean, isActive: boolean): string =>
    disabled
        ? 'bg-surface-subtle shadow-none text-text-muted'
        : clsx(
              isActive
                  ? 'bg-action-secondary-active text-action-on-secondary-active hover:text-action-on-secondary-active'
                  : 'bg-action-secondary text-action-on-secondary',
              'shadow-action-secondary hover:shadow-action-secondary-hover',
          );

const getSecondaryRecessedInnerClasses = (disabled: boolean, isActive: boolean): string =>
    disabled
        ? 'bg-surface-subtle shadow-none text-text-muted'
        : clsx(
              isActive
                  ? 'bg-action-secondary text-action-on-secondary-active hover:text-action-on-secondary-active shadow-action-secondary'
                  : 'bg-action-secondary text-action-on-secondary shadow-action-secondary',
          );

const getMutedColorClasses = (disabled: boolean): string =>
    disabled
        ? 'bg-action-muted text-action-on-muted'
        : 'bg-action-muted text-action-on-muted hover:bg-action-primary hover:text-action-on-primary';

const getDestructiveVariantClasses = (disabled: boolean): string =>
    clsx(
        'text-action-on-destructive',
        disabled ? 'bg-action-destructive' : 'bg-action-destructive hover:bg-action-destructive-hover',
    );

const getTertiaryVariantClasses = (disabled: boolean, isActive: boolean): string =>
    clsx(getTertiaryPressedSurfaceClasses(isActive, disabled), isActive ? 'text-accent' : 'text-text-secondary');

const getLinkVariantClasses = (disabled: boolean): string =>
    clsx(
        'bg-transparent no-underline',
        disabled ? 'text-text-disabled' : 'text-text-primary hover:text-link-hover hover:no-underline',
        'py-xs px-s',
    );

/* ============================================
 * Variant classes
 * ============================================ */
const LEGACY_VARIANT_TO_PRESENTATION: Record<LegacyButtonVariant, InternalButtonVariant> = {
    default: 'primary',
    solid: 'primary',
};

const LEGACY_VARIANTS = Object.keys(LEGACY_VARIANT_TO_PRESENTATION) as LegacyButtonVariant[];

const isLegacyVariant = (variant: ButtonOwnProps['variant']): variant is LegacyButtonVariant =>
    variant != null && LEGACY_VARIANTS.includes(variant as LegacyButtonVariant);

const resolvePresentation = (
    variant: ButtonOwnProps['variant'],
    color: ButtonOwnProps['color'],
): InternalButtonVariant => {
    if (variant === 'solid') {
        switch (color) {
            case 'danger':
                return 'destructive';
            case 'muted':
                return 'muted';
            case 'secondary':
                return 'secondary';
            default:
                return 'primary';
        }
    }

    if (variant == null) {
        return color === 'danger'
            ? 'destructive'
            : color === 'secondary'
              ? 'secondary'
              : color === 'muted'
                ? 'muted'
                : 'primary';
    }

    if (isLegacyVariant(variant)) {
        return LEGACY_VARIANT_TO_PRESENTATION[variant];
    }

    return variant;
};

const getVariantClasses = (
    variant: InternalButtonVariant,
    disabled: boolean,
    disabledVariant: ButtonProps['disabledVariant'],
    isActive: boolean,
    iconOnly: boolean,
    size: ButtonProps['size'],
): string => {
    switch (variant) {
        case 'primary':
            return getPrimaryVariantClasses(size, disabled, disabledVariant);
        case 'destructive':
            return getDestructiveVariantClasses(disabled);
        case 'muted':
            return getMutedColorClasses(disabled);
        case 'secondary':
            return clsx(getSecondaryColorClasses(disabled, isActive), iconOnly && size === 'sm' && 'rounded-full!');
        case 'tertiary':
            return getTertiaryVariantClasses(disabled, isActive);
        case 'link':
            return getLinkVariantClasses(disabled);
        case 'plain':
            return '';
    }
};

/** `plain` keeps only the focus ring and cursor, for controls that bring their whole look, such as cards. */
const plainClasses =
    'focus-visible:outline-solid focus-visible:outline-1 focus-visible:outline-focus-ring focus-visible:outline-offset-2';

const baseClasses = clsx(
    'inline-flex items-center justify-center',
    'border-none rounded-full outline-none',
    'font-sans font-medium no-underline',
    'transition-[background-color,color,box-shadow,opacity] duration-quick ease-standard',
    '[&>svg]:h-[14px] [&>svg]:w-[14px] [&>svg]:shrink-0 [&>svg]:transform-gpu [&>svg]:[backface-visibility:hidden]',
    'focus-visible:outline-solid focus-visible:outline-1 focus-visible:outline-focus-ring focus-visible:outline-offset-2',
);

export const Button = <E extends ElementType = 'button'>({
    activeSurface = 'recessed',
    as,
    children,
    className = '',
    color,
    disabled = false,
    disabledVariant,
    iconOnly = false,
    iconPosition,
    isActive = false,
    size,
    style,
    type = 'button',
    variant = 'solid',
    ...props
}: ButtonProps<E>): ReactElement => {
    const Component = as ?? 'button';
    const resolvedPresentation = resolvePresentation(variant, color);
    const buttonStyle =
        size === 'sm' && iconOnly && resolvedPresentation !== 'link' && resolvedPresentation !== 'plain'
            ? { ...BUTTON_SM_ICON_ONLY_SIZE_STYLE, ...style }
            : style;
    const shouldDimOnHover =
        resolvedPresentation !== 'plain' &&
        resolvedPresentation !== 'primary' &&
        resolvedPresentation !== 'secondary' &&
        resolvedPresentation !== 'tertiary';
    const useRecessedActiveSurface = activeSurface === 'recessed' && resolvedPresentation === 'secondary' && !disabled;
    const isPlain = resolvedPresentation === 'plain';

    return (
        <Component
            className={clsx(
                isPlain ? plainClasses : baseClasses,
                useRecessedActiveSurface
                    ? clsx(
                          isActive
                              ? clsx(
                                    'overflow-hidden p-0 bg-transparent',
                                    size === 'sm' && (iconOnly ? SMALL_ICON_ONLY_DIMENSIONS : 'min-h-control-sm'),
                                    '!rounded-full',
                                )
                              : clsx(
                                    getSizeClasses(size, iconOnly, iconPosition, resolvedPresentation),
                                    getSecondaryColorClasses(disabled, false),
                                ),
                      )
                    : isPlain
                      ? undefined
                      : clsx(
                            getSizeClasses(size, iconOnly, iconPosition, resolvedPresentation),
                            getVariantClasses(
                                resolvedPresentation,
                                disabled,
                                disabledVariant,
                                isActive,
                                iconOnly,
                                size,
                            ),
                        ),
                disabled ? 'opacity-50 cursor-default' : 'cursor-pointer',
                !disabled && shouldDimOnHover && 'hover:opacity-80',
                className,
            )}
            disabled={disabled}
            style={buttonStyle}
            type={Component === 'button' ? type : undefined}
            {...props}
        >
            {useRecessedActiveSurface && isActive ? (
                <RecessedSurface
                    active={true}
                    as="span"
                    className="!rounded-full"
                    placement="inside"
                    pocketTone="orange"
                    surfaceClassName={clsx(
                        'items-center justify-center',
                        getRecessedInnerSizeClasses(size, iconOnly, iconPosition),
                        getSecondaryRecessedInnerClasses(disabled, isActive),
                    )}
                    surfaceShadow="sm"
                    surfaceTone="gray"
                >
                    {children}
                </RecessedSurface>
            ) : (
                children
            )}
        </Component>
    );
};
